import {NgTemplateOutlet} from '@angular/common';
import {DatepickerDateRules} from '../datepicker-date-rules';
import {DatepickerDayContext, DatepickerDateFilter} from '../datepicker.models';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  isSignal,
  input,
  output,
  signal,
  TemplateRef,
  untracked,
  viewChildren,
} from '@angular/core';
import {LocalDate} from '@js-joda/core';
import {AUR_CALENDAR_LOCALE} from './calendar-locale';
import {CalendarDateUtil} from './calendar-date.util';
import {DatepickerValue} from '../datepicker.models';

/**
 * Сетка дней одного месяца без шапки и экранов месяцев/лет. Потребитель хранит значение и обрабатывает selectedChange.
 * Не открывает всплывающее окно и не обращается к форме или API приложения.
 */
@Component({
  selector: 'aur-calendar',
  imports: [NgTemplateOutlet],
  templateUrl: './aur-calendar.component.html',
  styleUrl: './aur-calendar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AurCalendarComponent {
  private static nextContentId = 0;
  protected readonly contentId = `aur-day-content-${AurCalendarComponent.nextContentId++}-`;
  public readonly dayContent = input<TemplateRef<DatepickerDayContext> | null>(null);
  /** День для отметки «сегодня» в сетке дней; null скрывает отметку. Зону определяет родитель. */
  public readonly today = input<LocalDate | null>(null);

  protected isEmptyWeek(week: readonly (LocalDate | null)[]): boolean {
    return week.every(day => day === null);
  }

  /** Выбранная дата; null означает отсутствие выбора. */
  public readonly selected = input<LocalDate | null>(null);
  /** Сообщает о выборе мышью, Enter или пробелом. Навигация событие не вызывает. */
  public readonly selectedChange = output<LocalDate>();
  /** Управляемый месяц для составных панелей; null оставляет собственную навигацию. */
  public readonly month = input<LocalDate | null>(null);
  /** Запрос смены месяца при переходе клавиатурой через границу месяца. */
  public readonly monthChange = output<LocalDate>();
  /** Диапазон для отображения; календарь не определяет правила его выбора. */
  public readonly range = input<DatepickerValue | null>(null);
  /** Дата под указателем или клавиатурным фокусом для предварительного выбора. */
  public readonly datePreview = output<LocalDate>();

  /** Минимальная дата включительно; null не ограничивает начало. */
  public readonly minDate = input<LocalDate | null>(null);
  /** Максимальная дата включительно; null не ограничивает конец. */
  public readonly maxDate = input<LocalDate | null>(null);
  /** Дополнительное правило доступности дня; не запрещает день внутри диапазона. */
  public readonly dateFilter = input<DatepickerDateFilter | null>(null);

  /** Внутренняя опора ежегодного режима; null сохраняет обычное перелистывание лет. */
  public readonly annualYear = input<number | null>(null);
  public readonly fixedSixWeeks = input(false);
  /** Дата фокуса при возврате из сетки месяцев; не является выбором. */
  public readonly focusDate = input<LocalDate | null>(null);

  private readonly localeSource = inject(AUR_CALENDAR_LOCALE);
  /** Статическая локаль переносимого ядра либо реактивная локаль приложения. */
  protected get locale() {
    return isSignal(this.localeSource) ? this.localeSource() : this.localeSource;
  }
  protected readonly activeDate = signal(LocalDate.now());
  protected readonly weeks = computed(() => {
    const weeks = CalendarDateUtil.weeks(this.activeDate());
    if (!this.fixedSixWeeks()) {
      return weeks;
    }
    return Array.from({length: 6}, (_, index) => weeks[index] ?? Array<LocalDate | null>(7).fill(null));
  });
  protected readonly monthLabel = computed(() => {
    const date = this.activeDate();
    return this.annualYear() === null
      ? `${this.locale.months[date.monthValue() - 1]} ${date.year()}`
      : this.locale.months[date.monthValue() - 1];
  });

  private readonly dayButtons = viewChildren<ElementRef<HTMLButtonElement>>('dayButton');
  private readonly pendingFocus = signal<string | null>(null);

  constructor() {
    effect(() => {
      const date = this.focusDate();
      if (date) {
        this.showDate(date);
        this.pendingFocus.set(date.toString());
      }
    });
    effect(() => {
      const selected = this.selected();
      if (selected && !this.month()) {
        this.activeDate.set(selected);
      }
    });
    effect(() => {
      const month = this.month();
      const active = untracked(this.activeDate);
      if (month && !month.withDayOfMonth(1).equals(active.withDayOfMonth(1))) {
        this.activeDate.set(month.withDayOfMonth(1));
      }
    });
    effect(() => {
      const target = this.pendingFocus();
      const button = this.dayButtons().find(item => item.nativeElement.dataset['date'] === target);
      if (target && button) {
        button.nativeElement.focus();
        this.pendingFocus.set(null);
      }
    });
  }

  /** Текущий день клавиатурной навигации; нужен родителю для сохранения дня при перелистывании. */
  public get navigationDate(): LocalDate {
    return this.activeDate();
  }

  /** Отображает рассчитанную родителем дату, не меняя выбор и не перехватывая фокус шапки. */
  public showDate(date: LocalDate): void {
    this.activeDate.set(date);
  }

  protected isDisabled(date: LocalDate): boolean {
    return !DatepickerDateRules.isSelectable(date, this.minDate(), this.maxDate(), this.dateFilter());
  }

  protected isEndpoint(date: LocalDate): boolean {
    const range = this.range();
    return !!(range && (range.start.equals(date) || range.end.equals(date))) || !!this.selected()?.equals(date);
  }

  protected isInRange(date: LocalDate): boolean {
    const range = this.range();
    return !!range && !date.isBefore(range.start) && !date.isAfter(range.end);
  }

  protected select(date: LocalDate): void {
    if (!DatepickerDateRules.isSelectable(date, this.minDate(), this.maxDate(), this.dateFilter())) {
      return;
    }
    this.activeDate.set(date);
    this.selectedChange.emit(date);
  }

  protected dayLabel(date: LocalDate): string {
    const label = `${date.dayOfMonth()} ${this.locale.months[date.monthValue() - 1]}`;
    return this.annualYear() === null ? `${label} ${date.year()}` : label;
  }

  /** И мышь, и клавиатура проходят одну границу: декабрь/январь остаются в опорном году. */
  private inNavigationYear(date: LocalDate): LocalDate {
    return this.annualYear() === null ? date : date.withYear(this.annualYear());
  }

  protected onKeydown(event: KeyboardEvent, date: LocalDate): void {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
      return;
    }
    const target = CalendarDateUtil.navigate(date, event.key);
    const next = target ? this.inNavigationYear(target) : null;
    if (next) {
      event.preventDefault();
      this.activeDate.set(next);
      this.pendingFocus.set(next.toString());
      if (!next.withDayOfMonth(1).equals(date.withDayOfMonth(1))) {
        this.monthChange.emit(next.withDayOfMonth(1));
      }
    }
  }
}
